import { HubspotForm } from "@components/atoms/HubspotForm"
import { Logo } from "@components/atoms/Logo"
import { Appear } from "@components/motion/Appear"
import { useAppData } from "eddev/hooks"
import { Link, useRoute } from "eddev/routing"
import { cn } from "tailwind-variants"

export function Footer() {
  const menus = useAppData((appData) => appData.menus?.nodes)
  const footerMenu = menus?.find((menu) => menu?.slug === "footer-menu")
  const topLevelFooterMenuItems = footerMenu?.menuItems?.nodes.filter((item) => !item?.parentId) || []
  const legalMenu = menus?.find((menu) => menu?.slug === "footer-legal-menu")
  const socialMenu = menus?.find((menu) => menu?.slug === "footer-social-menu")
  const route = useRoute()
  const newsletterShown = [
    "/",
    "/contact",
    "/insights",
    "/leadership",
    "/careers",
    "/risk-and-security",
    "/careers",
  ].includes(route.pathname.split("/")[1] ? `/${route.pathname.split("/")[1]}` : "/")

  return (
    <>
      {newsletterShown && <Newsletter />}
      <div className="w-full content-sized mt-10 mb-10 flex flex-col gap-8 md:gap-10">
        <div className="w-full flex flex-col md:flex-row items-start justify-between gap-8 md:gap-0">
          <div className="w-full md:w-cols-3 flex justify-between items-center">
            <Logo />
            <SocialMenu className="md:hidden" />
          </div>
          {topLevelFooterMenuItems?.map((item) => {
            if (!item) return null
            return (
              <div key={`footer-item-${item.label}`} className="w-full md:w-cols-3 flex flex-col gap-5 md:gap-3">
                <p className="type-sans-base-semibold text-pretty">{item.label}</p>
                {item.childItems?.nodes.map((childItem) => {
                  if (!childItem) return null
                  const cookieProps = childItem.url?.includes("#cookiesettings")
                    ? {
                        id: "ot-sdk-btn",
                        className: "ot-sdk-show-settings",
                        onClick: (e: React.MouseEvent) => {
                          e.preventDefault()
                        },
                      }
                    : {}
                  return (
                    <Link
                      href={childItem.url || "#"}
                      key={`footer-child-item-${childItem.label}`}
                      {...cookieProps}
                      className={cn(
                        "!type-sans-base-regular md:!type-sans-sm-regular text-slate600 underline decoration-transparent md:hover:decoration-current transition-all !text-current !bg-transparent !m-0 !p-0",
                        cookieProps.className,
                      )}
                    >
                      {childItem.label}
                    </Link>
                  )
                })}
              </div>
            )
          })}
        </div>
        <div className="w-full border-t border-slate300 pt-8 md:pt-5 flex items-center justify-between">
          <div className="flex flex-col md:flex-row md:items-center justify-center gap-5 md:gap-7">
            {legalMenu?.menuItems?.nodes.map((item) => {
              if (!item) return null
              const isLink = item.url && item.url !== "#"
              const LinkEl = isLink ? Link : "div"
              const linkProps = isLink ? { href: item.url } : {}
              return (
                <LinkEl
                  {...linkProps}
                  key={`legal-item-${item.label}`}
                  className={cn(
                    "type-sans-base-regular text-slate600",
                    isLink && "underline decoration-transparent md:hover:decoration-current transition-all",
                  )}
                >
                  {item.label}
                </LinkEl>
              )
            })}
          </div>
          <SocialMenu className="hidden md:flex" />
        </div>
      </div>
    </>
  )
}

const SocialMenu = ({ className }: { className?: string }) => {
  const menus = useAppData((appData) => appData.menus?.nodes)
  const socialMenu = menus?.find((menu) => menu?.slug === "footer-social-menu")

  return (
    <div className={cn("flex items-center justify-center gap-3 md:gap-2", className)}>
      {socialMenu?.menuItems?.nodes.map((item) => {
        if (!item) return null
        return (
          <Link
            href={item.url || "#"}
            key={`social-item-${item.label}`}
            target="_blank"
            rel="noreferrer noopener"
            className="type-serif-sm-regular text-slate400 md:hover:text-slate600 transition-colors flex items-center justify-center"
          >
            {item.label === "X" && (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M16.1973 5H18.5723L13.3837 10.9303L19.4877 19H14.7083L10.9649 14.1057L6.68165 19H4.30524L9.85498 12.6569L3.99939 5H8.90011L12.2838 9.47354L16.1973 5ZM15.3638 17.5785H16.6798L8.18503 6.34687H6.77283L15.3638 17.5785Z"
                  fill="currentColor"
                />
              </svg>
            )}
            {item.label === "LinkedIn" && (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18.8156 4H5.18125C4.52812 4 4 4.51563 4 5.15313V18.8438C4 19.4813 4.52812 20 5.18125 20H18.8156C19.4688 20 20 19.4813 20 18.8469V5.15313C20 4.51563 19.4688 4 18.8156 4ZM8.74687 17.6344H6.37188V9.99687H8.74687V17.6344ZM7.55938 8.95625C6.79688 8.95625 6.18125 8.34062 6.18125 7.58125C6.18125 6.82188 6.79688 6.20625 7.55938 6.20625C8.31875 6.20625 8.93437 6.82188 8.93437 7.58125C8.93437 8.3375 8.31875 8.95625 7.55938 8.95625ZM17.6344 17.6344H15.2625V13.9219C15.2625 13.0375 15.2469 11.8969 14.0281 11.8969C12.7937 11.8969 12.6062 12.8625 12.6062 13.8594V17.6344H10.2375V9.99687H12.5125V11.0406H12.5437C12.8594 10.4406 13.6344 9.80625 14.7875 9.80625C17.1906 9.80625 17.6344 11.3875 17.6344 13.4438V17.6344Z"
                  fill="currentColor"
                />
              </svg>
            )}
            {item.label === "YouTube" && (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19.8922 8.58928C19.8922 8.58928 19.7256 7.41294 19.2124 6.89642C18.5626 6.21661 17.8361 6.21327 17.5029 6.17329C15.1169 6 11.5345 6 11.5345 6H11.5279C11.5279 6 7.94554 6 5.55953 6.17329C5.22629 6.21327 4.49983 6.21661 3.85001 6.89642C3.33682 7.41294 3.17353 8.58928 3.17353 8.58928C3.17353 8.58928 3.00024 9.97223 3.00024 11.3518V12.6448C3.00024 14.0244 3.1702 15.4074 3.1702 15.4074C3.1702 15.4074 3.33682 16.5837 3.84668 17.1003C4.4965 17.7801 5.34959 17.7567 5.72949 17.83C7.09577 17.96 11.5312 18 11.5312 18C11.5312 18 15.1169 17.9933 17.5029 17.8234C17.8361 17.7834 18.5626 17.7801 19.2124 17.1003C19.7256 16.5837 19.8922 15.4074 19.8922 15.4074C19.8922 15.4074 20.0622 14.0278 20.0622 12.6448V11.3518C20.0622 9.97223 19.8922 8.58928 19.8922 8.58928ZM9.76836 14.2144V9.41905L14.3771 11.825L9.76836 14.2144Z"
                  fill="currentColor"
                />
              </svg>
            )}
            {item.label === "Instagram" && (
              <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15.25 4.5H7.75C5.67893 4.5 4 6.17893 4 8.25V15.75C4 17.8211 5.67893 19.5 7.75 19.5H15.25C17.3211 19.5 19 17.8211 19 15.75V8.25C19 6.17893 17.3211 4.5 15.25 4.5Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.5003 11.5272C14.5928 12.1514 14.4862 12.7889 14.1956 13.349C13.905 13.9091 13.4451 14.3633 12.8815 14.647C12.3178 14.9307 11.6791 15.0294 11.0561 14.9292C10.4331 14.8289 9.85759 14.5348 9.4114 14.0886C8.96521 13.6424 8.67107 13.0669 8.57082 12.4439C8.47057 11.8209 8.56932 11.1822 8.85301 10.6185C9.13671 10.0549 9.59091 9.59504 10.151 9.30442C10.7111 9.01379 11.3486 8.90718 11.9728 8.99974C12.6095 9.09415 13.1989 9.39084 13.654 9.84597C14.1092 10.3011 14.4059 10.8905 14.5003 11.5272Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.625 7.875H15.6344"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </Link>
        )
      })}
    </div>
  )
}

const Newsletter = () => {
  return (
    <div className={cn("w-full my-blockSpace bg-slate200 relative py-blockSpace")}>
      <div
        className="w-full h-headerPattern absolute left-0 bottom-[-1px]"
        style={{
          backgroundSize: "auto 100%",
          backgroundRepeat: "repeat-x",
          backgroundImage: `url("${env.themePath}/assets/images/patterns/1_reverse.svg")`,
        }}
      />

      <Appear
        slide
        className="w-full content-sized relative bg-slate950 text-slate50 p-7 md:p-5 rounded-lg grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-0"
      >
        <div className="w-full">
          <Appear as="p" className="type-serif-2-xl-regular md:type-serif-2-5-xl-regular text-balance">
            Stay up to date with the latest finance insights
          </Appear>
        </div>
        <div className="w-full flex flex-col">
          <Appear delay={100} className="relative w-full">
            {/* <input
                  className="w-full type-sans-lg-semibold md:type-sans-xl-semibold block appearance-none bg-transparent py-6 md:py-4 border-b border-b-slate400 placeholder:opacity-100 placeholder:text-white outline-none pr-7 rounded-none"
                  placeholder="Enter your email address"
                /> */}
            {/* <svg
                  className="w-8 h-8 md:w-6 md:h-6 absolute right-0 top-5 md:top-4 text-slate400 cursor-pointer md:hover:text-white transition-colors"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="0.5" y="0.5" width="31" height="31" rx="15.5" stroke="currentColor" />
                  <path
                    d="M20.2533 16.5235H9.75V15.4765H20.2533L15.264 10.4873L16 9.75L22.25 16L16 22.25L15.264 21.5127L20.2533 16.5235Z"
                    fill="currentColor"
                  />
                </svg> */}
            <HubspotForm
              portalId="22005419"
              formId="e100da17-a434-470d-bb27-73ce190b0bbf"
              raw
              className="newsletter-form"
            />
          </Appear>

          <Appear delay={150} as="p" className="type-serif-sm-regular text-balance text-slate400 mt-8 md:mt-6 md:mb-4">
            By subscribing, you agree to receive Bakkt updates and insights. You may unsubscribe at any time.
          </Appear>
        </div>
      </Appear>
    </div>
  )
}
